import type { IntlShape } from 'react-intl'
import type { SolutionType } from '@app/src/types/resourceExplorer'

/**
 * Shared utility functions and types for template availability management
 */

export interface OrganizationAvailability {
  id: number
  organizationId: number
  organization?: { id: number; name: string }
}

export interface LinkedTemplate {
  id: number
  name: string
  solutionAvailability?: SolutionType[]
  organizationAvailability?: OrganizationAvailability[]
}

export interface BaseTemplate {
  id: number
  name: string
  solutionAvailability?: SolutionType[]
  organizationAvailability?: OrganizationAvailability[]
  linkedTemplates?: LinkedTemplate[]
}

/**
 * Get localized label for a solution type
 */
export const getSolutionTypeLabel = (solutionType: string, formatMessage: IntlShape['formatMessage']) => {
  return formatMessage({ id: `solutions.${solutionType}` })
}

/**
 * Sort solution types alphabetically by their localized labels
 */
export const getSortedSolutionTypes = (
  solutionTypes: string[] | Set<string>,
  formatMessage: IntlShape['formatMessage'],
  excludeWildcard = true,
) => {
  const typesArray = Array.from(solutionTypes)
  const filtered = excludeWildcard ? typesArray.filter(t => t !== '*') : typesArray
  const labelMap = new Map(filtered.map(t => [t, getSolutionTypeLabel(t, formatMessage)]))
  return filtered.sort((a, b) => (labelMap.get(a) ?? '').localeCompare(labelMap.get(b) ?? ''))
}

/**
 * Sort items with a name property alphabetically by name
 */
export const getSortedOrganizations = <T extends { name: string }>(organizations: T[]): T[] => {
  return [...organizations].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Check if template is available to all solutions and organizations
 */
export const isAvailableToAll = (
  solutionAvailability?: string[] | Set<string>,
  organizationAvailability?: OrganizationAvailability[] | Set<number>,
) => {
  const hasNoSolutionRestrictions =
    !solutionAvailability ||
    (Array.isArray(solutionAvailability) ? solutionAvailability.length === 0 : solutionAvailability.has('*'))

  const hasNoOrgRestrictions =
    !organizationAvailability ||
    (Array.isArray(organizationAvailability) ? organizationAvailability.length === 0 : organizationAvailability.has(-1))

  return hasNoSolutionRestrictions && hasNoOrgRestrictions
}
